import { SetStateAction, useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm, SubmitHandler } from "react-hook-form";
import OtpInput from "react-otp-input";
import { z } from "zod";
import { Button } from "components/ui/button/Button";
import { otp } from "utilities/formSchemas";
import "./authentication.scss";
import {
  usePostDeviceAuthenticationMutation,
  usePostValidateDeviceAuthMutation,
  usePostEmailOtpValidationMutation,
  DeviceType,
} from "./authenticationService";
import { useDispatch, useSelector } from "react-redux";
import { getCurrentUserId, setCredentials } from "features/authSlice";
import { useRedirect } from "hooks/useRedirect";
import Overlay from "components/ui/loading-spinners/Overlay/Overlay";
import { ErrorType } from "utilities/error";
import { INVALID_OTP_ERROR, LoginError, OTP_EXPIRED_ERROR } from "utilities/Constant";
import { getResendCodeDeviceId, getAuthDeviceType, setResendCodeDeviceId } from "features/mfaSlice";
import { useNavigate } from "react-router-dom";
import { ResendCooldownButton } from "components/ui/ResendCooldownButton";
import { clearCooldown } from "features/resendCooldownSlice";
import Footer from "components/footer/Footer";

interface FormData {
  otp: string;
}

const formSchema = z.object({ otp });

export default function RegistrationAuth() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userId = useSelector(getCurrentUserId);
  const deviceAuthId = useSelector(getResendCodeDeviceId);
  const deviceType = useSelector(getAuthDeviceType);
  const isEmailDevice = deviceType.toUpperCase() === "EMAIL";
  useRedirect(userId === "" && deviceAuthId === "", "/");

  const [sendOtpRequest, { data: sendOtpData, isSuccess: isSendOtpSuccess }] = usePostDeviceAuthenticationMutation();

  useEffect(() => {
    if (isSendOtpSuccess && sendOtpData) {
      dispatch(setResendCodeDeviceId(sendOtpData.id));
    }
  }, [isSendOtpSuccess, sendOtpData]);

  const [error, setError] = useState({ status: 0, code: "" });
  const {
    handleSubmit,
    control,
    clearErrors,
    formState: { errors, isSubmitted },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: "onSubmit", // Only validate on submit
    reValidateMode: "onSubmit", // Re-validate only on submit
  });

  const [sendDeviceOtpRequest, { isLoading: isDeviceOtpLoading, isError: isDeviceOtpError }] = usePostValidateDeviceAuthMutation();

  const [sendEmailOtpRequest, { isLoading: isEmailOtpLoading, isError: isEmailOtpError }] = usePostEmailOtpValidationMutation();

  const onSubmit: SubmitHandler<FormData> = (value) => {
    setError({ status: 0, code: "" });

    if (isEmailDevice) {
      return sendEmailOtpRequest({
        deviceAuthId: deviceAuthId,
        otp: value.otp,
        userId: userId,
      })
        .unwrap()
        .then((data) => {
          dispatch(setCredentials(data));
          dispatch(clearCooldown());
          navigate("/my-loans", { replace: true });
        })
        .catch((error: ErrorType) => {
          setError((prev) => ({
            ...prev,
            status: error.status,
            code: error.data.code.toLowerCase(),
          }));
        });
    } else {
      return sendDeviceOtpRequest({
        otp: value.otp,
        deviceAuthId: deviceAuthId,
        userId: userId,
      })
        .unwrap()
        .then((data) => {
          dispatch(setCredentials(data));
          dispatch(clearCooldown());
          navigate("/my-loans", { replace: true });
        })
        .catch((error: ErrorType) => {
          setError((prev) => ({
            ...prev,
            status: error.status,
            code: error.data.code.toLowerCase(),
          }));
        });
    }
  };

  const isLoading = isDeviceOtpLoading || isEmailOtpLoading;
  const isError = isDeviceOtpError || isEmailOtpError;

  let ErrorMessage = "";

  if (isError && error.status === 400) {
    if (error.code === "invalid otp") {
      ErrorMessage = INVALID_OTP_ERROR;
    } else {
      ErrorMessage = OTP_EXPIRED_ERROR;
    }
  }

  const handleResendCode = () => {
    return sendOtpRequest({ deviceAuthId: deviceAuthId, DeviceType: isEmailDevice ? DeviceType.Email : DeviceType.Sms });
  };

  if (isLoading) return <Overlay />;

  return (
    <>
      <div className="account">
        <form onSubmit={handleSubmit(onSubmit)} className="account-form">
          <div className="account-form__title">Please verify your account</div>
          <div className="account-form__digit">Enter your 6-digit code below</div>
          {isError &&
            (error.status === 400 ? (
              <p className="error__message1">{ErrorMessage}</p>
            ) : (
              <div className="error__container">
                {LoginError.map((error) => (
                  <p key={error} className="error__message2">
                    {error}
                  </p>
                ))}
              </div>
            ))}
          {isSubmitted && errors.otp && <p className="error__message1">{errors.otp.message}</p>}
          <div>
            <Controller
              name="otp"
              control={control}
              render={({ field: { onChange, value } }) => (
                <div className="account-form--input">
                  <OtpInput
                    inputStyle="account-form--input-field"
                    onChange={(e: SetStateAction<string>) => {
                      onChange(e);
                      if (isSubmitted) {
                        clearErrors("otp");
                      }
                    }}
                    value={value}
                    numInputs={6}
                    isInputNum
                    separator={<span className="separator">&mdash;</span>}
                    containerStyle="account-form--container-field"
                  />
                </div>
              )}
            />
          </div>
          <Button title="Continue" size="large" className="account-form__button1" />
        </form>
        <ResendCooldownButton onResend={handleResendCode}>
          Resend Code
        </ResendCooldownButton>
      </div>
      <div className="registration-auth-footer">
        <Footer />
      </div>
    </>
  );
}
